// thin typed fetch wrapper — all API calls go through here
// credentials: 'include' on every request for httpOnly session cookies

const BASE_URL = import.meta.env.VITE_API_URL || '';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const options: RequestInit = {
    method,
    credentials: 'include',
    headers: {} as Record<string, string>,
  };

  if (body !== undefined) {
    (options.headers as Record<string, string>)['Content-Type'] = 'application/json';
    options.body = JSON.stringify(body);
  }

  let res: Response;
  try {
    res = await fetch(url, options);
  } catch (err) {
    console.error(`[api] network error ${method} ${path}:`, err);
    throw new ApiError('Network error — could not reach the server', 0);
  }

  // handle 204 no-content
  if (res.status === 204) {
    return undefined as T;
  }

  let data: unknown;
  try {
    data = await res.json();
  } catch {
    console.error(`[api] invalid json ${method} ${path}, status ${res.status}`);
    throw new ApiError('Invalid response from server', res.status);
  }

  if (!res.ok) {
    const msg =
      (data as Record<string, unknown>)?.error ??
      (data as Record<string, unknown>)?.message ??
      `Request failed with status ${res.status}`;
    console.error(`[api] ${res.status} ${method} ${path}:`, msg);
    throw new ApiError(String(msg), res.status, data);
  }

  return data as T;
}

// convenience methods
export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};

// file upload — sends multipart/form-data (no JSON Content-Type)
export async function uploadFile(file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${BASE_URL}/uploads`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new ApiError(
      (data as Record<string, string>).error ?? `Upload failed (${res.status})`,
      res.status,
      data,
    );
  }
  const data = await res.json() as { url: string };
  return data.url;
}

// ── auth ──

import type {
  AuthUser,
  Tenant,
  TenantDetail,
  TenantUpdate,
  ServerCreate,
  Plan,
  PlanCreate,
  PlanUpdate,
  PaymentSession,
  PaymentStats,
  JournalEntry,
  Subscriber,
  SubscriberStats,
  Affiliate,
  AffiliateCreate,
  AdminUser,
  AdminTenant,
  AdminPayment,
  AdminRole,
  PlatformHealth,
  BotHealth,
} from '../types/index.js';

export const auth = {
  me: () => api.get<AuthUser>('/auth/me'),
  logout: () => api.post<{ ok: boolean }>('/auth/logout'),
  totpSetup: () => api.post<{ qrCode: string; secret: string }>('/auth/totp/setup'),
  totpVerify: (token: string) => api.post<{ ok: boolean }>('/auth/totp/verify', { token }),
  totpValidate: (token: string) => api.post<{ ok: boolean }>('/auth/totp/validate', { token }),
};

// ── tenants ──

export const tenants = {
  list: () => api.get<{ tenants: Tenant[] }>('/tenants'),
  get: (id: string) => api.get<{ tenant: TenantDetail }>(`/tenants/${id}`),
  create: (data: ServerCreate) =>
    api.post<{ tenant: TenantDetail }>('/tenants', data),
  update: (id: string, data: TenantUpdate) =>
    api.patch<{ tenant: TenantDetail }>(`/tenants/${id}`, data),
  setBotToken: (id: string, token: string) =>
    api.post<{ ok: boolean }>(`/tenants/${id}/bot-token`, { token }),
  botHealth: (id: string) =>
    api.get<BotHealth>(`/tenants/${id}/bot-health`),
  discordRoles: (id: string) =>
    api.get<{ roles: { id: string; name: string; color: string }[] }>(`/tenants/${id}/discord-roles`),
};

// ── plans ──

export const plans = {
  list: (tenantId: string) =>
    api.get<{ plans: Plan[] }>(`/tenants/${tenantId}/plans`),
  create: (tenantId: string, data: PlanCreate) =>
    api.post<{ plan: Plan }>(`/tenants/${tenantId}/plans`, data),
  update: (tenantId: string, planId: string, data: PlanUpdate) =>
    api.patch<{ plan: Plan }>(`/tenants/${tenantId}/plans/${planId}`, data),
  delete: (tenantId: string, planId: string) =>
    api.delete<{ ok: boolean }>(`/tenants/${tenantId}/plans/${planId}`),
};

// ── payments ──

export const payments = {
  list: (tenantId: string, params?: { state?: string; limit?: number; offset?: number }) => {
    const query = new URLSearchParams();
    if (params?.state) query.set('state', params.state);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const qs = query.toString();
    return api.get<{ payments: PaymentSession[] }>(
      `/tenants/${tenantId}/payments${qs ? `?${qs}` : ''}`,
    );
  },
  stats: (tenantId: string) =>
    api.get<PaymentStats>(`/tenants/${tenantId}/payments/stats`),
  journal: (tenantId: string, paymentId: string) =>
    api.get<{ journal: JournalEntry[] }>(`/tenants/${tenantId}/payments/${paymentId}/journal`),
};

// ── subscribers ──

export const subscribers = {
  list: (tenantId: string, params?: { status?: string; limit?: number; offset?: number }) => {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const qs = query.toString();
    return api.get<{ subscribers: Subscriber[]; total: number }>(
      `/tenants/${tenantId}/subscribers${qs ? `?${qs}` : ''}`,
    );
  },
  stats: (tenantId: string) =>
    api.get<SubscriberStats>(`/tenants/${tenantId}/subscribers/stats`),
  cancel: (tenantId: string, userId: string) =>
    api.patch<{ ok: boolean }>(`/tenants/${tenantId}/subscribers/${userId}/cancel`, {}),
  extend: (tenantId: string, userId: string, days: number) =>
    api.patch<{ ok: boolean; newExpiresAt: string }>(`/tenants/${tenantId}/subscribers/${userId}/extend`, { days }),
  changePlan: (tenantId: string, userId: string, planId: string) =>
    api.patch<{ ok: boolean; newPlan: string; newExpiresAt: string }>(`/tenants/${tenantId}/subscribers/${userId}/change-plan`, { planId }),
};

// ── affiliates ──

export const affiliates = {
  list: (tenantId: string) =>
    api.get<{ affiliates: Affiliate[] }>(`/tenants/${tenantId}/affiliates`),
  create: (tenantId: string, data: AffiliateCreate) =>
    api.post<{ affiliate: Affiliate }>(`/tenants/${tenantId}/affiliates`, data),
  update: (tenantId: string, affiliateId: string, data: { commissionPct?: number; active?: boolean }) =>
    api.patch<{ affiliate: Affiliate }>(`/tenants/${tenantId}/affiliates/${affiliateId}`, data),
  delete: (tenantId: string, affiliateId: string) =>
    api.delete<{ ok: boolean }>(`/tenants/${tenantId}/affiliates/${affiliateId}`),
};

// ── admin ──

export const admin = {
  users: () => api.get<{ users: AdminUser[] }>('/admin/users'),
  tenants: () => api.get<{ tenants: AdminTenant[] }>('/admin/tenants'),
  createServerForUser: (data: { name: string; guildId: string; ownerId: string }) =>
    api.post<{ tenant: { id: string } }>('/admin/tenants/create', data),
  suspendServer: (tenantId: string) =>
    api.patch<{ ok: boolean }>(`/admin/tenants/${tenantId}/suspend`, {}),
  activateServer: (tenantId: string) =>
    api.patch<{ ok: boolean }>(`/admin/tenants/${tenantId}/activate`, {}),
  whitelist: (data: { discordId: string }) =>
    api.post<{ ok: boolean; userId: string }>('/admin/whitelist', data),
  revokeAccess: (data: { userId: string }) =>
    api.delete<{ ok: boolean }>('/admin/whitelist', data),
  payments: (params?: { state?: string; limit?: number; offset?: number }) => {
    const query = new URLSearchParams();
    if (params?.state) query.set('state', params.state);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const qs = query.toString();
    return api.get<{ payments: AdminPayment[]; stateCounts: Record<string, number> }>(
      `/admin/payments${qs ? `?${qs}` : ''}`,
    );
  },
  forceTransition: (paymentId: string, data: { newState: string; reason: string }) =>
    api.post<{ ok: boolean; oldState: string; newState: string }>(`/admin/payments/${paymentId}/force-transition`, data),
  retrySweep: (paymentId: string) =>
    api.post<{ ok: boolean; txHash?: string; amount?: number; message?: string }>(`/admin/payments/${paymentId}/retry-sweep`),
  roles: (filter?: string) => {
    const qs = filter ? `?filter=${filter}` : '';
    return api.get<{ roles: AdminRole[] }>(`/admin/roles${qs}`);
  },
  deactivateRole: (roleId: string) =>
    api.patch<{ ok: boolean }>(`/admin/roles/${roleId}/deactivate`, {}),
  extendRole: (roleId: string, days: number) =>
    api.patch<{ ok: boolean }>(`/admin/roles/${roleId}/extend`, { days }),
  health: () => api.get<PlatformHealth>('/admin/health'),
};
